// Label printing — supports iPad (MobiPrint/.zpl download) and desktop (browser print dialog)

const API_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Detect if running on iOS/iPadOS
 */
function isIOS(): boolean {
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Code 128B bar patterns — each symbol is 6 alternating bar/space widths.
 * Index 0-94 = printable chars (ASCII 32-126), 104=StartB, 105=StartC, 106=Stop
 */
const CODE128_PATTERNS: number[][] = [
  [2,1,2,2,2,2],[2,2,2,1,2,2],[2,2,2,2,2,1],[1,2,1,2,2,3],[1,2,1,3,2,2],
  [1,3,1,2,2,2],[1,2,2,2,1,3],[1,2,2,3,1,2],[1,3,2,2,1,2],[2,2,1,2,1,3],
  [2,2,1,3,1,2],[2,3,1,2,1,2],[1,1,2,2,3,2],[1,2,2,1,3,2],[1,2,2,2,3,1],
  [1,1,3,2,2,2],[1,2,3,1,2,2],[1,2,3,2,2,1],[2,2,3,2,1,1],[2,2,1,1,3,2],
  [2,2,1,2,3,1],[2,1,3,2,1,2],[2,2,3,1,1,2],[3,1,2,1,3,1],[3,1,1,2,2,2],
  [3,2,1,1,2,2],[3,2,1,2,2,1],[3,1,2,2,1,2],[3,2,2,1,1,2],[3,2,2,2,1,1],
  [2,1,2,1,2,3],[2,1,2,3,2,1],[2,3,2,1,2,1],[1,1,1,3,2,3],[1,3,1,1,2,3],
  [1,3,1,3,2,1],[1,1,2,3,1,3],[1,3,2,1,1,3],[1,3,2,3,1,1],[2,1,1,3,1,3],
  [2,3,1,1,1,3],[2,3,1,3,1,1],[1,1,2,1,3,3],[1,1,2,3,3,1],[1,3,2,1,3,1],
  [1,1,3,1,2,3],[1,1,3,3,2,1],[1,3,3,1,2,1],[3,1,3,1,2,1],[2,1,1,3,3,1],
  [2,3,1,1,3,1],[2,1,3,1,1,3],[2,1,3,3,1,1],[2,1,3,1,3,1],[3,1,1,1,2,3],
  [3,1,1,3,2,1],[3,3,1,1,2,1],[3,1,2,1,1,3],[3,1,2,3,1,1],[3,3,2,1,1,1],
  [3,1,4,1,1,1],[2,2,1,4,1,1],[4,3,1,1,1,1],[1,1,1,2,2,4],[1,1,1,4,2,2],
  [1,2,1,1,2,4],[1,2,1,4,2,1],[1,4,1,1,2,2],[1,4,1,2,2,1],[1,1,2,2,1,4],
  [1,1,2,4,1,2],[1,2,2,1,1,4],[1,2,2,4,1,1],[1,4,2,1,1,2],[1,4,2,2,1,1],
  [2,4,1,2,1,1],[2,2,1,1,1,4],[4,1,3,1,1,1],[2,4,1,1,1,2],[1,3,4,1,1,1],
  [1,1,1,2,4,2],[1,2,1,1,4,2],[1,2,1,2,4,1],[1,1,4,2,1,2],[1,2,4,1,1,2],
  [1,2,4,2,1,1],[4,1,1,2,1,2],[4,2,1,1,1,2],[4,2,1,2,1,1],[2,1,2,1,4,1],
  [2,1,4,1,2,1],[4,1,2,1,2,1],[1,1,1,1,4,3],[1,1,1,3,4,1],[1,3,1,1,4,1],
  [1,1,4,1,1,3],[1,1,4,3,1,1],[4,1,1,1,1,3],[4,1,1,3,1,1],[1,1,3,1,4,1],
  [1,1,4,1,3,1],[3,1,1,1,4,1],[4,1,1,1,3,1],[2,1,1,4,1,2],[2,1,1,2,1,4],
  [2,1,1,2,3,2],[2,3,3,1,1,1,2],
];
// Stop pattern (index 106) is 7 bars: [2,3,3,1,1,1,2]
const STOP_PATTERN = [2,3,3,1,1,1,2];

/**
 * Encode text as Code 128B and return SVG barcode markup.
 */
function encodeCode128SVG(text: string, height: number = 40, moduleWidth: number = 1): string {
  if (!text) return '';

  // Build symbol values: StartB + characters + checksum + Stop
  const values: number[] = [104]; // Start B
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i) - 32;
    if (code < 0 || code > 94) continue; // skip non-printable
    values.push(code);
  }

  // Calculate check digit
  let checksum = values[0];
  for (let i = 1; i < values.length; i++) {
    checksum += values[i] * i;
  }
  values.push(checksum % 103);

  // Convert to bar widths
  const bars: number[] = [];
  for (const val of values) {
    const pattern = CODE128_PATTERNS[val];
    if (pattern) bars.push(...pattern);
  }
  bars.push(...STOP_PATTERN);

  // Calculate total width
  const totalUnits = bars.reduce((a, b) => a + b, 0);
  const svgWidth = totalUnits * moduleWidth;

  // Build SVG rects
  let x = 0;
  let rects = '';
  for (let i = 0; i < bars.length; i++) {
    const w = bars[i] * moduleWidth;
    if (i % 2 === 0) {
      // Even index = black bar
      rects += `<rect x="${x}" y="0" width="${w}" height="${height}" fill="black"/>`;
    }
    x += w;
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${height}" viewBox="0 0 ${svgWidth} ${height}">${rects}</svg>`;
}

/**
 * Parse ZPL into structured label data: text lines + barcode data.
 */
function parseZplContent(zpl: string): { lines: string[]; barcodeData: string | null } {
  const lines: string[] = [];
  let barcodeData: string | null = null;

  const segments = zpl.split('^FS');
  for (const seg of segments) {
    const fdIdx = seg.lastIndexOf('^FD');
    if (fdIdx === -1) continue;
    const text = seg.substring(fdIdx + 3).trim();
    if (!text) continue;
    const beforeFd = seg.substring(0, fdIdx);
    if (/\^BC/.test(beforeFd)) {
      barcodeData = text;
    } else {
      lines.push(text);
    }
  }

  return { lines, barcodeData };
}

/**
 * Detect label size from ZPL commands.
 * ^PW = print width in dots, ^LL = label length in dots (300 DPI)
 */
function detectLabelSize(zpl: string): { width: string; height: string; small: boolean } {
  const pwMatch = zpl.match(/\^PW(\d+)/);
  const llMatch = zpl.match(/\^LL(\d+)/);
  const pw = pwMatch ? parseInt(pwMatch[1]) : 1200;
  const ll = llMatch ? parseInt(llMatch[1]) : 1800;
  const widthIn = Math.round(pw / 300);
  const heightIn = Math.round(ll / 300);
  const small = widthIn <= 3 && heightIn <= 2;
  return { width: `${widthIn}in`, height: `${heightIn}in`, small };
}

/**
 * Open a browser print dialog with label content rendered as HTML + SVG barcode.
 * Auto-sizes to label dimensions (3x1 for material, 4x6 for shipping/final product, etc.)
 */
function printLabelViaBrowser(zpl: string): void {
  const { lines, barcodeData } = parseZplContent(zpl);
  const title = lines[0] || 'Label';
  const details = lines.slice(1);
  const size = detectLabelSize(zpl);

  // Scale barcode and fonts based on label size
  const titleSize = size.small ? '9pt' : '16pt';
  const lineSize = size.small ? '8pt' : '12pt';
  const padding = size.small ? '2px 4px' : '8px 12px';
  const barcodeHeight = size.small ? 30 : 50;
  const barcodeModule = size.small ? 1 : 2;

  const barcodeSvg = barcodeData ? encodeCode128SVG(barcodeData, barcodeHeight, barcodeModule) : '';

  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (!printWindow) return;

  printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Print Label</title>
  <style>
    @page {
      size: ${size.width} ${size.height};
      margin: 0;
    }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Arial Narrow', Arial, Helvetica, sans-serif;
      background: white;
      color: black;
      width: ${size.width};
      height: ${size.height};
      overflow: hidden;
    }
    .label {
      width: ${size.width};
      height: ${size.height};
      padding: ${padding};
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    .title {
      font-size: ${titleSize};
      font-weight: bold;
      line-height: 1.1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      ${size.small ? '' : 'border-bottom: 2px solid black; padding-bottom: 4px; margin-bottom: 6px;'}
    }
    .barcode {
      margin: ${size.small ? '2px 0' : '6px 0'};
      line-height: 0;
    }
    .barcode-text {
      font-family: 'Courier New', monospace;
      font-size: ${size.small ? '7pt' : '10pt'};
      margin-top: 1px;
    }
    .line {
      font-size: ${lineSize};
      font-weight: bold;
      line-height: ${size.small ? '1.2' : '1.5'};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .print-hint {
      position: fixed;
      bottom: 10px;
      left: 0;
      right: 0;
      text-align: center;
      font-size: 10pt;
      color: #999;
    }
    @media print {
      .print-hint { display: none; }
    }
  </style>
</head>
<body>
  <div class="label">
    <div class="title">${title.replace(/</g, '&lt;')}</div>
    ${barcodeSvg ? `<div class="barcode">${barcodeSvg}<div class="barcode-text">${barcodeData!.replace(/</g, '&lt;')}</div></div>` : ''}
    ${details.map(l => `<div class="line">${l.replace(/</g, '&lt;')}</div>`).join('\n    ')}
  </div>
  <div class="print-hint">Select your label printer and click Print. Close this window when done.</div>
  <script>
    window.onload = function() { window.print(); };
  </script>
</body>
</html>`);
  printWindow.document.close();
}

/**
 * Send ZPL to printer — auto-detects platform.
 * iOS/iPad: .zpl file download (Zebra Printer Setup Utility / MobiPrint)
 * Desktop: Browser print dialog with HTML-rendered label + SVG barcode
 */
export async function sendToMobiPrint(zpl: string): Promise<void> {
  if (isIOS()) {
    sendToZebraPrinter(zpl);
  } else {
    printLabelViaBrowser(zpl);
  }
}

/**
 * Download ZPL as .zpl file — Zebra Printer Setup Utility on iPad opens it.
 */
export function sendToZebraPrinter(zpl: string): void {
  const blob = new Blob([zpl], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `label-${Date.now()}.zpl`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Queue a label via API, fetch ZPL, and print.
 * Auto-detects platform: iOS → .zpl download, Desktop → browser print dialog.
 */
export async function queueAndPrint(workOrderId: string, labelType: string, workstationId?: string): Promise<{ success: boolean; message: string }> {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  // Queue the label
  const queueRes = await fetch(`${API_URL}/labels/queue`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ workOrderId, labelType, copies: 1, workstationId }),
  });
  if (!queueRes.ok) {
    const err = await queueRes.json().catch(() => ({}));
    return { success: false, message: err.message || 'Failed to queue label' };
  }
  const label = await queueRes.json();

  // Fetch ZPL and send to appropriate printer
  const zplRes = await fetch(`${API_URL}/labels/${label.id}/zpl`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (zplRes.ok) {
    const { zpl } = await zplRes.json();
    await sendToMobiPrint(zpl);
  }

  const method = isIOS() ? 'Zebra printer' : 'browser print';
  return { success: true, message: `Label sent to ${method}` };
}
